import { RunState, CuratedCity } from '../types/run';
import { Card, Item, Player, RoundResult, GameState } from '../types/game';
import { createStarterDeck, createCard, DRAFTABLE_CARD_KEYS, CARD_TEMPLATES } from '../data/cards';
import { CURATED_CITIES } from '../data/cities';
import { tuning } from '../data/tuning';
import { generateRunMap, getNode } from './runMap';
import { RUN_MAP_VERSION } from '../types/runMap';
import {
  PASSIVE_TEMPLATES,
  PassiveTemplate,
  ALL_PASSIVE_KEYS,
  STARTER_PASSIVE_KEYS,
  PASSIVE_DRAFT_POOL_SIZE,
  hasPassive,
} from '../data/passives';

// Re-export so existing callers of `from './runState'` keep working.
export { hasPassive };

const RUN_STORAGE_KEY = 'geo-sleeper-run';

// --- Lifecycle ---

export function createRunState(): RunState {
  const enabledCities = CURATED_CITIES.filter(c => !c.disabled);
  return {
    runId: `run-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    round: 1,
    cityHistory: [],
    deck: createStarterDeck(),
    droppedCards: [],
    marks: 0,
    unbankedMarks: 0,
    collectedItems: [],
    safehouseCardsThisRound: [],
    passives: [],
    lastRoundResult: null,
    shopDraftCount: 0,
    shopPackCounts: { intel: 0, tactics: 0, fieldcraft: 0 },
    directBuyUsed: false,
    savedAt: Date.now(),
    usedCodenames: [],
    // Ship 1: every new run generates a branching path map. The player
    // picks one of the entry nodes on the RunMapScreen to start act 1.
    runMap: generateRunMap(enabledCities),
    currentNodeId: null,
    completedNodeIds: [],
  };
}

// --- Persistence ---

export function saveRunState(run: RunState): void {
  try {
    const toSave = { ...run, savedAt: Date.now() };
    localStorage.setItem(RUN_STORAGE_KEY, JSON.stringify(toSave));
  } catch { /* storage full — silently ignore */ }
}

export function loadRunState(): RunState | null {
  try {
    const raw = localStorage.getItem(RUN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RunState;
    // Ensure new fields have defaults for older saves
    if (!parsed.safehouseCardsThisRound) parsed.safehouseCardsThisRound = [];
    if (!parsed.passives) parsed.passives = [];
    if (!parsed.shopPackCounts) parsed.shopPackCounts = { intel: 0, tactics: 0, fieldcraft: 0 };
    if (parsed.directBuyUsed === undefined) parsed.directBuyUsed = false;
    // Ship 1 migration: runs from before the branching map are incompatible —
    // too many flow assumptions change (pickNextCity, drop-card phase, etc.)
    // to shim cleanly. Wipe them so the user starts fresh on the new system.
    if (!parsed.runMap || parsed.runMap.version !== RUN_MAP_VERSION) {
      try { localStorage.removeItem(RUN_STORAGE_KEY); } catch { /* ignore */ }
      return null;
    }
    if (!parsed.completedNodeIds) parsed.completedNodeIds = [];
    return parsed;
  } catch {
    return null;
  }
}

export function deleteRunState(): void {
  try {
    localStorage.removeItem(RUN_STORAGE_KEY);
  } catch { /* ignore */ }
}

export function hasSavedRun(): boolean {
  return !!localStorage.getItem(RUN_STORAGE_KEY);
}

// --- City selection ---

export function pickNextCity(run: RunState): CuratedCity {
  // Exclude disabled cities (not yet precomputed on R2) and cities already played this run.
  const enabled = CURATED_CITIES.filter(c => !c.disabled);
  const available = enabled.filter(c => !run.cityHistory.includes(c.name));
  // If run history has exhausted the enabled pool (long run), reset — but still only
  // pick from enabled, never from disabled.
  const pool = available.length > 0 ? available : enabled;
  return pool[Math.floor(Math.random() * pool.length)];
}

// --- Round transitions ---

/** Merge player's hand + deck + discard into one deck for the next round (exclude burn pile). */
export function mergeDeckForNextRound(player: Player): Card[] {
  return [...player.hand, ...player.deck, ...player.discard];
}

/** Apply round-end state changes: record result, add marks, handle death penalty. */
export function applyRoundEnd(
  run: RunState,
  result: RoundResult,
  earnedMarks: number,
  collectedItems: Item[],
): RunState {
  // Bag Man passive: marks earned during a round are increased by 25%.
  // Applied before death penalty, so even a death push still keeps the bonus
  // on the fraction that survives.
  const bagManMultiplier = hasPassive(run, 'bagMan') ? 1.25 : 1;
  const grossEarned = Math.floor(earnedMarks * bagManMultiplier);

  // Kill marks bank IMMEDIATELY. Killing the agent is a terminal achievement
  // and the reward shouldn't sit at-risk in the unbanked pool waiting for HQ
  // — the run is already safe once the kill lands. Escape / extract marks
  // still flow through unbanked because the player could hypothetically lose
  // them on a later death-push.
  let banked = run.marks;
  let unbanked = run.unbankedMarks;
  if (result === 'kill') {
    banked = banked + grossEarned;
  } else {
    unbanked = unbanked + grossEarned;
  }

  if (result === 'death') {
    unbanked = Math.floor(unbanked * (1 - tuning.marks.deathPenaltyPct));
  }

  return {
    ...run,
    lastRoundResult: result,
    marks: banked,
    unbankedMarks: unbanked,
    collectedItems: [...run.collectedItems, ...collectedItems],
  };
}

// --- Card drop ---

/**
 * Pick random cards from the deck for the forced drop choice.
 *
 * Default pool size is 3; callers can pass a different count to honour the
 * Severance passive (which shows 5 instead). Drop is skipped entirely if the
 * deck is too small per `tuning.marks.minDeckSize`.
 */
export function getCardDropOptions(deck: Card[], count: number = 3): Card[] {
  if (deck.length <= tuning.marks.minDeckSize) return []; // skip drop
  const shuffled = [...deck].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/** Remove the chosen card from the deck and send it to HQ. */
export function applyCardDrop(run: RunState, cardId: string): RunState {
  const card = run.deck.find(c => c.id === cardId);
  if (!card) return run;
  return {
    ...run,
    deck: run.deck.filter(c => c.id !== cardId),
    droppedCards: [...run.droppedCards, { card, roundDropped: run.round }],
  };
}

// --- Passives ---

/** Add a passive to the run. Idempotent — re-adding a passive is a no-op. */
export function addPassive(run: RunState, key: string): RunState {
  if (!PASSIVE_TEMPLATES[key]) return run;
  if (run.passives.includes(key)) return run;
  return { ...run, passives: [...run.passives, key] };
}

/**
 * Pick N passive options for a draft. Excludes passives the run already owns.
 * When runStart is true, pulls from the starter-friendly subset so the first
 * pick biases toward loadout passives and broad utility rather than
 * build-around passives that need a specific deck to function.
 */
export function getPassiveDraftOptions(
  run: RunState,
  runStart: boolean,
  count: number = PASSIVE_DRAFT_POOL_SIZE,
): PassiveTemplate[] {
  const sourcePool = runStart ? STARTER_PASSIVE_KEYS : ALL_PASSIVE_KEYS;
  const owned = new Set(run.passives);
  const available = sourcePool.filter(k => !owned.has(k));
  // Fall back to the full pool if the starter pool is drained (edge case —
  // a player can't realistically exhaust either pool in one run, but the
  // fallback keeps the draft screen from ever showing zero options).
  const pool = available.length > 0
    ? available
    : ALL_PASSIVE_KEYS.filter(k => !owned.has(k));
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length))
    .map(k => PASSIVE_TEMPLATES[k]);
}

// --- HQ ---

/** Bank all unbanked marks (safe now). */
export function bankMarks(run: RunState): RunState {
  return {
    ...run,
    marks: run.marks + run.unbankedMarks,
    unbankedMarks: 0,
  };
}

/** Sell collected items, add their value to unbanked marks, clear the item list. */
export function sellItems(run: RunState): { run: RunState; total: number } {
  // Bank Job passive: item extract values are increased by 50%.
  const bankJobMultiplier = hasPassive(run, 'bankJob') ? 1.5 : 1;
  const total = Math.floor(
    run.collectedItems.reduce((sum, item) => sum + (item.sellValue ?? 10), 0) * bankJobMultiplier,
  );
  return {
    run: {
      ...run,
      unbankedMarks: run.unbankedMarks + total,
      collectedItems: [],
    },
    total,
  };
}

/** Recover all dropped cards back into the deck. */
export function recoverDroppedCards(run: RunState): RunState {
  const recovered = run.droppedCards.map(dc => dc.card);
  return {
    ...run,
    deck: [...run.deck, ...recovered],
    droppedCards: [],
  };
}

/** Get the current draft cost based on how many drafts have been done this HQ visit. */
export function getShopDraftCost(run: RunState): number {
  const base = tuning.marks.shopDraftBaseCost;
  const mult = Math.pow(tuning.marks.shopDraftCostMultiplier, run.shopDraftCount);
  return Math.floor(base * mult);
}

/** Draft 3 random cards for the shop pick. */
export function getShopDraftOptions(): Card[] {
  const shuffled = [...DRAFTABLE_CARD_KEYS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3).map(k => createCard(k));
}

/** Buy a drafted card: add to deck, deduct marks, increment draft count. */
export function buyDraftCard(run: RunState, card: Card): RunState {
  const cost = getShopDraftCost(run);
  if (run.marks < cost) return run;
  return {
    ...run,
    deck: [...run.deck, card],
    marks: run.marks - cost,
    shopDraftCount: run.shopDraftCount + 1,
  };
}

// ─── Pack-based shop (replaces single-draft system) ──────

export type PackType = 'intel' | 'tactics' | 'fieldcraft';

/** Get the cost for a specific pack type based on how many of that type have been opened. */
export function getPackCost(run: RunState, packType: PackType): number {
  const base = tuning.marks.shopDraftBaseCost;
  const count = run.shopPackCounts?.[packType] ?? 0;
  return Math.floor(base * Math.pow(tuning.marks.shopDraftCostMultiplier, count));
}

/** Draw N random cards from a specific category for the pack.
 * Defaults to 3; callers pass 5 when Severance is owned. */
export function getPackOptions(packType: PackType, count: number = 3): Card[] {
  const keys = DRAFTABLE_CARD_KEYS.filter(k => {
    const cat = CARD_TEMPLATES[k]?.category;
    return cat === packType;
  });
  const shuffled = [...keys].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length)).map(k => createCard(k));
}

/** How many options to show in a card draft (HQ pack or Extraction reward). */
export function getDraftOptionCount(run: RunState): number {
  return hasPassive(run, 'severance') ? 5 : 3;
}

/** Roll an extraction-op reward: pick a random category, draw N cards. */
export function getExtractionRewardOptions(run: RunState): { category: PackType; cards: Card[] } {
  const categories: PackType[] = ['intel', 'tactics', 'fieldcraft'];
  const category = categories[Math.floor(Math.random() * categories.length)];
  const count = getDraftOptionCount(run);
  return { category, cards: getPackOptions(category, count) };
}

/** Buy a card from an opened pack: add to deck, deduct marks, increment that pack's counter. */
export function buyPackCard(run: RunState, card: Card, packType: PackType): RunState {
  const cost = getPackCost(run, packType);
  if (run.marks < cost) return run;
  return {
    ...run,
    deck: [...run.deck, card],
    marks: run.marks - cost,
    shopPackCounts: {
      ...run.shopPackCounts,
      [packType]: (run.shopPackCounts?.[packType] ?? 0) + 1,
    },
  };
}

/** Pay for a pack without picking a card (player opened it but declined all 3). */
export function payPackCost(run: RunState, packType: PackType): RunState {
  const cost = getPackCost(run, packType);
  if (run.marks < cost) return run;
  return {
    ...run,
    marks: run.marks - cost,
    shopPackCounts: {
      ...run.shopPackCounts,
      [packType]: (run.shopPackCounts?.[packType] ?? 0) + 1,
    },
  };
}

/** Direct buy: add a Movement or Shortcut card at a fixed price. One per HQ visit. */
export function buyDirectCard(run: RunState, templateKey: 'move' | 'shortcut'): RunState {
  if (run.directBuyUsed) return run;
  const cost = templateKey === 'move'
    ? tuning.marks.shopDirectMoveCost
    : tuning.marks.shopDirectShortcutCost;
  if (run.marks < cost) return run;
  const card = createCard(templateKey);
  return {
    ...run,
    deck: [...run.deck, card],
    marks: run.marks - cost,
    directBuyUsed: true,
  };
}

/** Remove a card from the deck permanently. */
export function removeCard(run: RunState, cardId: string): RunState {
  if (run.deck.length <= tuning.marks.minDeckSize) return run;
  if (run.marks < tuning.marks.shopRemovalCost) return run;
  return {
    ...run,
    deck: run.deck.filter(c => c.id !== cardId),
    marks: run.marks - tuning.marks.shopRemovalCost,
  };
}

/** Prepare for next round: increment round counter, record city, reset shop count. */
export function prepareNextRound(run: RunState, cityName: string): RunState {
  return {
    ...run,
    round: run.round + 1,
    cityHistory: [...run.cityHistory, cityName],
    shopDraftCount: 0,
    shopPackCounts: { intel: 0, tactics: 0, fieldcraft: 0 },
    directBuyUsed: false,
    collectedItems: [],
    safehouseCardsThisRound: [],
    lastRoundResult: null,
    midRoundSave: null,
  };
}

// --- Run map progression helpers (Ship 1) ---

/**
 * Advance the player to a chosen map node. Validates the node is actually
 * reachable from the current position; if not, returns the run unchanged.
 *
 * Reachability rules:
 *   - Pre-run (no currentNodeId): must be in act 1 entryNodeIds.
 *   - Current is a completed row-6 node: must be in act2EntryNodeIds.
 *   - Otherwise: must be in current.nextNodeIds.
 */
export function advanceToNode(run: RunState, nodeId: string): RunState {
  const map = run.runMap;
  if (!map) return run;
  const target = getNode(map, nodeId);
  if (!target) return run;
  const currentId = run.currentNodeId ?? null;
  const completed = run.completedNodeIds ?? [];

  if (currentId == null) {
    if (!map.entryNodeIds.includes(nodeId)) return run;
  } else {
    const current = getNode(map, currentId);
    if (!current) return run;
    const isInterActTransition =
      current.row === 6 && completed.includes(currentId);
    if (isInterActTransition) {
      if (!(map.act2EntryNodeIds ?? []).includes(nodeId)) return run;
    } else {
      if (!current.nextNodeIds.includes(nodeId)) return run;
    }
  }
  return { ...run, currentNodeId: nodeId };
}

/** Mark the current node as completed — called after a round/HQ finishes. */
export function markCurrentNodeCompleted(run: RunState): RunState {
  if (!run.currentNodeId) return run;
  const completed = run.completedNodeIds ?? [];
  if (completed.includes(run.currentNodeId)) return run;
  return {
    ...run,
    completedNodeIds: [...completed, run.currentNodeId],
  };
}

/** Save the current in-round game state so the player can resume later. */
export function saveMidRound(
  run: RunState,
  gameState: GameState,
  cityName: string,
  cityLat: number,
  cityLng: number,
): void {
  const updated: RunState = {
    ...run,
    midRoundSave: { gameState, cityName, cityLat, cityLng },
  };
  saveRunState(updated);
}

/** Clear mid-round save (called when round completes normally). */
export function clearMidRoundSave(run: RunState): RunState {
  return { ...run, midRoundSave: null };
}
